// Quick guide — Irregular verbs (present tense), grouped by the pattern of their
// irregularity. Most "irregular" verbs aren't random — they fall into a few
// families. Each family now shows a FULL model conjugation so the learner sees
// the whole shape, not just the odd form. Hand-drawn paper style with audio.
import { speakButtonHTML } from "../lib/audio.js";

const PRONOUNS = ["yo", "tú", "él / ella", "nosotros", "vosotros", "ellos / ellas"];

// A full 6-form conjugation table. `hi` is the set of row indices to highlight
// (the irregular / "boot" forms); highlighted rows get the rose treatment.
function conjTable(forms, hi) {
  const set = new Set(hi || []);
  const rows = forms
    .map((form, i) => {
      const on = set.has(i);
      return `
        <div class="flex items-baseline gap-2 py-0.5 px-1.5 rounded ${on ? "bg-rose-50" : ""}">
          <span class="w-24 shrink-0 text-sage-500 text-base" style="font-family:Inter,sans-serif">${PRONOUNS[i]}</span>
          <span class="font-bold ${on ? "text-rose-700" : "text-sage-800"}">${form}</span>${speakButtonHTML(form)}
        </div>`;
    })
    .join("");
  return `<div class="mt-1">${rows}</div>`;
}

// A family card: title, note, the model verb's infinitive, and its full table.
function familyCard({ color, title, note, inf, gloss, forms, hi, extra }) {
  return `
    <div class="sketch-box-sm bg-white/30 p-4 mb-4 break-inside-avoid">
      <h3 class="${color} text-2xl sketch-underline inline-block">${title}</h3>
      <p class="text-sm text-sage-500 mb-2 mt-1" style="font-family:Inter,sans-serif">${note}</p>
      <p class="text-lg text-sage-700">Model: <b class="text-sage-800">${inf}</b> <span class="text-base text-sage-500" style="font-family:Inter,sans-serif">— ${gloss}</span>${speakButtonHTML(inf)}</p>
      ${conjTable(forms, hi)}
      ${extra || ""}
    </div>`;
}

// Stem-change families — one model verb each, boot forms highlighted. The boot
// is yo · tú · él · ellos (everything except nosotros / vosotros = rows 0,1,2,5).
const BOOT = [0, 1, 2, 5];

const STEM_FAMILIES = [
  {
    color: "text-emerald-700",
    title: "e → ie",
    note: "The stressed e becomes ie inside the boot.",
    inf: "querer",
    gloss: "to want",
    forms: ["quiero", "quieres", "quiere", "queremos", "queréis", "quieren"],
    hi: BOOT,
  },
  {
    color: "text-emerald-700",
    title: "o → ue",
    note: "The stressed o becomes ue inside the boot.",
    inf: "poder",
    gloss: "to be able / can",
    forms: ["puedo", "puedes", "puede", "podemos", "podéis", "pueden"],
    hi: BOOT,
  },
  {
    color: "text-emerald-700",
    title: "e → i",
    note: "Only in some -ir verbs: the stressed e becomes i in the boot.",
    inf: "pedir",
    gloss: "to ask for",
    forms: ["pido", "pides", "pide", "pedimos", "pedís", "piden"],
    hi: BOOT,
  },
  {
    color: "text-emerald-700",
    title: "u → ue",
    note: "Just one common verb does this.",
    inf: "jugar",
    gloss: "to play",
    forms: ["juego", "juegas", "juega", "jugamos", "jugáis", "juegan"],
    hi: BOOT,
  },
];

const OTHER_FAMILIES = [
  {
    color: "text-amber-700",
    title: "“Yo” irregulars (-go)",
    note: "Only the yo form is odd — the rest are regular.",
    inf: "hacer",
    gloss: "to do / make",
    forms: ["hago", "haces", "hace", "hacemos", "hacéis", "hacen"],
    hi: [0],
    extra: `<p class="text-sm text-sage-500 mt-2" style="font-family:Inter,sans-serif">Same -go yo form: <b>poner</b> → pongo · <b>salir</b> → salgo · <b>traer</b> → traigo · <b>decir</b> → digo.</p>`,
  },
  {
    color: "text-violet-700",
    title: "-go + stem change",
    note: "Some verbs combine a -go yo form with a boot stem change.",
    inf: "tener",
    gloss: "to have",
    forms: ["tengo", "tienes", "tiene", "tenemos", "tenéis", "tienen"],
    hi: [0, 1, 2, 5],
    extra: `<p class="text-sm text-sage-500 mt-2" style="font-family:Inter,sans-serif">Note yo = <b>tengo</b> (-go), and e → ie everywhere else in the boot. <b>venir</b> works the same → vengo, vienes…</p>`,
  },
  {
    color: "text-sky-700",
    title: "Spelling change (-zco)",
    note: "Verbs ending -cer / -cir add a z before the c in the yo form to keep the sound.",
    inf: "conocer",
    gloss: "to know (a person/place)",
    forms: ["conozco", "conoces", "conoce", "conocemos", "conocéis", "conocen"],
    hi: [0],
    extra: `<p class="text-sm text-sage-500 mt-2" style="font-family:Inter,sans-serif">Same family: <b>conducir</b> → conduzco · <b>traducir</b> → traduzco · <b>ofrecer</b> → ofrezco.</p>`,
  },
  {
    color: "text-rose-600",
    title: "Totally irregular — ser",
    note: "No pattern — learn the whole shape by heart.",
    inf: "ser",
    gloss: "to be",
    forms: ["soy", "eres", "es", "somos", "sois", "son"],
    hi: [0, 1, 2, 3, 4, 5],
  },
  {
    color: "text-rose-600",
    title: "Totally irregular — ir",
    note: "Also fully irregular — and note it looks nothing like “ir”.",
    inf: "ir",
    gloss: "to go",
    forms: ["voy", "vas", "va", "vamos", "vais", "van"],
    hi: [0, 1, 2, 3, 4, 5],
  },
];

export function irregularVerbsView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Irregular Verbs ⚡</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Present tense. Most “irregular” verbs aren't random — they belong to a few families. Learn the family and one model verb in full, and the rest fall into line. Rose-tinted rows are the forms that break from the regular pattern.</p>

      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg mb-6">
        <h3 class="text-2xl text-emerald-700 sketch-underline inline-block">1 · Stem-changers (the “boot” 👢)</h3>
        <p class="text-sage-700 mt-1 mb-3">The stressed vowel changes in every form <b>except nosotros / vosotros</b>. Shade in the changed forms and they make a boot shape — that's why these are called boot verbs.</p>
        <div class="columns-1 sm:columns-2 gap-4">
          ${STEM_FAMILIES.map(familyCard).join("")}
        </div>
      </div>

      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg">
        <h3 class="text-2xl text-amber-700 sketch-underline inline-block">2 · The other families</h3>
        <p class="text-sage-700 mt-1 mb-3">These break in the <b>yo</b> form, or — for ser and ir — everywhere.</p>
        <div class="columns-1 sm:columns-2 gap-4">
          ${OTHER_FAMILIES.map(familyCard).join("")}
        </div>
      </div>

      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">ser · estar · tener · hacer get their own deep-dive in <b>The Big 4 Verbs</b>. For any verb's full conjugation across all tenses, use the Verb Index.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
